import { lazy } from 'react';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Users,
  Users2,
  Settings,
  BarChart3,
  Calendar,
  Bell,
  User,
  Home,
  Building2,
  ClipboardList,
  FileText,
  ClipboardCheck,
  MapPin,
  History,
} from 'lucide-react';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const TaskDetailsPage = lazy(() => import('@/pages/TaskDetailsPage'));
const CreateTaskPage = lazy(() => import('@/pages/CreateTaskPage'));
const EditTaskPage = lazy(() => import('@/pages/EditTaskPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('@/pages/ProjectDetailsPage'));
const CreateProjectPage = lazy(() => import('@/pages/CreateProjectPage'));
const EditProjectPage = lazy(() => import('@/pages/EditProjectPage'));
const TeamsPage = lazy(() => import('@/pages/TeamsPage'));
const TeamDetailsPage = lazy(() => import('@/pages/TeamDetailsPage'));
const CreateTeamPage = lazy(() => import('@/pages/CreateTeamPage'));
const EditTeamPage = lazy(() => import('@/pages/EditTeamPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const UserDetailsPage = lazy(() => import('@/features/users/pages/UserDetailsPage'));
const CreateUserPage = lazy(() => import('@/features/users/pages/CreateUserPage'));
const EditUserPage = lazy(() => import('@/features/users/pages/EditUserPage'));
const OrganizationsPage = lazy(() => import('@/pages/OrganizationsPage'));
const OrganizationDetailsPage = lazy(() => import('@/features/organizations/pages/OrganizationDetailsPage'));
const CreateOrganizationPage = lazy(() => import('@/features/organizations/pages/CreateOrganizationPage'));
const EditOrganizationPage = lazy(() => import('@/features/organizations/pages/EditOrganizationPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const DailyPlanPage = lazy(() => import('@/pages/DailyPlanPage'));
const DailyReportPage = lazy(() => import('@/pages/DailyReportPage'));
const ControlMapPage = lazy(() => import('@/pages/ControlMapPage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const WorkLocationsPage = lazy(() => import('@/pages/WorkLocationsPage'));
const ActivityLogPage = lazy(() => import('@/pages/ActivityLogPage'));

// Route types
export interface RouteConfig {
  path: string;
  element: React.LazyExoticComponent<React.ComponentType<any>>;
  label?: string;
  icon?: React.ComponentType<any>;
  protected?: boolean;
  requiredRole?: string;
  requiredPermission?: string;
  showInSidebar?: boolean;
  badge?: number;
}

// Public routes (accessible without authentication)
export const publicRoutes: RouteConfig[] = [
  {
    path: '/',
    element: HomePage,
    label: 'Home',
    icon: Home,
    protected: false,
    showInSidebar: false,
  },
  {
    path: '/login',
    element: LoginPage,
    label: 'Login',
    protected: false,
    showInSidebar: false,
  },
  {
    path: '/forgot-password',
    element: ForgotPasswordPage,
    label: 'Esqueceu a senha',
    protected: false,
    showInSidebar: false,
  },
  {
    path: '/reset-password',
    element: ResetPasswordPage,
    label: 'Redefinir senha',
    protected: false,
    showInSidebar: false,
  },
];

// Protected routes (require authentication)
export const protectedRoutes: RouteConfig[] = [
  {
    path: '/dashboard',
    element: DashboardPage,
    label: 'Dashboard',
    icon: LayoutDashboard,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/daily-plan',
    element: DailyPlanPage,
    label: 'Plano do Dia',
    icon: ClipboardList,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/daily-report',
    element: DailyReportPage,
    label: 'Relatório do Dia',
    icon: FileText,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/tasks',
    element: TasksPage,
    label: 'Tarefas',
    icon: CheckSquare,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/tasks/new',
    element: CreateTaskPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/tasks/:id',
    element: TaskDetailsPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/tasks/:id/edit',
    element: EditTaskPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/projects',
    element: ProjectsPage,
    label: 'Projectos',
    icon: FolderKanban,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/projects/new',
    element: CreateProjectPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/projects/:id',
    element: ProjectDetailsPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/projects/:id/edit',
    element: EditProjectPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/teams',
    element: TeamsPage,
    label: 'Equipas',
    icon: Users2,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/teams/new',
    element: CreateTeamPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/teams/:id',
    element: TeamDetailsPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/teams/:id/edit',
    element: EditTeamPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/attendance',
    element: AttendancePage,
    label: 'Presenças',
    icon: MapPin,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/calendar',
    element: CalendarPage,
    label: 'Calendário',
    icon: Calendar,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/activity-log',
    element: ActivityLogPage,
    label: 'Atividades',
    icon: History,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/notifications',
    element: NotificationsPage,
    label: 'Notificações',
    icon: Bell,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/profile',
    element: ProfilePage,
    label: 'Perfil',
    icon: User,
    protected: true,
    showInSidebar: false,
  },
];

// Manager routes (accessible to admin + manager)
export const managerRoutes: RouteConfig[] = [
  {
    path: '/control-map',
    element: ControlMapPage,
    label: 'Mapa de Controle',
    icon: ClipboardCheck,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/reports',
    element: ReportsPage,
    label: 'Relatórios',
    icon: BarChart3,
    protected: true,
    showInSidebar: true,
  },
];

// Admin-only routes
export const adminRoutes: RouteConfig[] = [
  {
    path: '/work-locations',
    element: WorkLocationsPage,
    label: 'Locais de Trabalho',
    icon: MapPin,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/settings',
    element: SettingsPage,
    label: 'Configurações',
    icon: Settings,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/users',
    element: UsersPage,
    label: 'Utilizadores',
    icon: Users,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/users/:id',
    element: UserDetailsPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/users/new',
    element: CreateUserPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/users/:id/edit',
    element: EditUserPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/organizations',
    element: OrganizationsPage,
    label: 'Organizações',
    icon: Building2,
    protected: true,
    showInSidebar: true,
  },
  {
    path: '/organizations/:id',
    element: OrganizationDetailsPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/organizations/new',
    element: CreateOrganizationPage,
    protected: true,
    showInSidebar: false,
  },
  {
    path: '/organizations/:id/edit',
    element: EditOrganizationPage,
    protected: true,
    showInSidebar: false,
  },
];

// 404 Not Found route
export const notFoundRoute = {
  path: '*',
  element: NotFoundPage,
  label: '404',
  protected: false,
};

// Get all routes
export const allRoutes = [...publicRoutes, ...protectedRoutes, ...managerRoutes, ...adminRoutes];

// Get sidebar routes
export const sidebarRoutes = protectedRoutes.filter(route => route.showInSidebar);

// Helper functions
export const isProtectedRoute = (path: string): boolean => {
  return [...protectedRoutes, ...managerRoutes, ...adminRoutes].some(route => route.path === path);
};

export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return allRoutes.find(route => route.path === path);
};